/**
 * AuthCallback — handles /auth/callback redirect from the OAuth flow.
 * Exchanges the authorization code for tokens and redirects to /.
 *
 * №14.1.1-C: folded off the sixth orphan design system onto `app-chrome`.
 */

import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { handleCallback } from "../auth";
import { useV3Theme } from "../../v3/hooks/use-v3-theme";
import { AuthShell } from "./AuthShell";
import { AuthSpinner } from "./AuthSpinner";

export function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const handled = useRef(false);
  useV3Theme();

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const errorParam = searchParams.get("error");

    if (errorParam) {
      setError(`Authentication failed: ${errorParam}`);
      return;
    }

    if (!code || !state) {
      setError("Missing authorization code or state.");
      return;
    }

    handleCallback(code, state).then((ok) => {
      if (ok) {
        navigate("/", { replace: true });
      } else {
        setError("Token exchange failed. Please try again.");
      }
    });
  }, [searchParams, navigate]);

  if (error) {
    return (
      <AuthShell>
        <div className="authpane__card">
          <h1>Something went wrong</h1>
          <p className="authpane__sub">{error}</p>
          <a className="btn btn--primary" href="/">Try again</a>
        </div>
      </AuthShell>
    );
  }

  return <AuthSpinner label="Signing in…" />;
}
